
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface MetaContextType {
  isConnected: boolean;
  isConnecting: boolean;
  accessToken: string | null;
  selectedCampaignId: string | null;
  setConnected: (connected: boolean) => void;
  setAccessToken: (token: string | null) => void;
  setSelectedCampaignId: (campaignId: string | null) => void;
  setIsConnecting: (connecting: boolean) => void;
  disconnect: () => void;
  lastTokenCheck: number;
  setLastTokenCheck: (time: number) => void;
  connectionErrors: number;
  incrementConnectionErrors: () => void;
  resetConnectionErrors: () => void;
}

const META_TOKEN_KEY = 'meta_access_token';
const META_CAMPAIGN_KEY = 'meta_selected_campaign_id';
const META_CONNECTED_KEY = 'meta_is_connected';
const META_TOKEN_CHECK_KEY = 'meta_last_token_check';

const MetaContext = createContext<MetaContextType | undefined>(undefined);

export const MetaProvider = ({ children }: { children: ReactNode }) => {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
  const [lastTokenCheck, setLastTokenCheck] = useState<number>(0);
  const [connectionErrors, setConnectionErrors] = useState<number>(0);

  // Load connection state from localStorage on initial render
  useEffect(() => {
    const storedToken = localStorage.getItem(META_TOKEN_KEY);
    const storedCampaignId = localStorage.getItem(META_CAMPAIGN_KEY);
    const storedConnected = localStorage.getItem(META_CONNECTED_KEY);
    const storedTokenCheck = localStorage.getItem(META_TOKEN_CHECK_KEY);
    
    if (storedToken) {
      setAccessToken(storedToken);
      console.log("Loaded saved access token");
    }
    
    if (storedCampaignId) {
      setSelectedCampaignId(storedCampaignId);
      console.log("Loaded saved campaign ID:", storedCampaignId);
    }
    
    if (storedConnected === 'true') {
      setIsConnected(true);
      console.log("Loaded connected state: connected");
      
      // Try to auto-connect on startup if needed
      if (storedToken && (!storedTokenCheck || Date.now() - parseInt(storedTokenCheck || "0", 10) > 24 * 60 * 60 * 1000)) {
        console.log("Token check is outdated, will validate on startup");
      }
    }
    
    if (storedTokenCheck) {
      setLastTokenCheck(parseInt(storedTokenCheck, 10));
    }
  }, []);
  
  // Save connection state to localStorage when it changes
  useEffect(() => {
    if (accessToken) {
      localStorage.setItem(META_TOKEN_KEY, accessToken);
    } else {
      localStorage.removeItem(META_TOKEN_KEY);
    }
  }, [accessToken]);
  
  // Save selected campaign ID to localStorage
  useEffect(() => {
    if (selectedCampaignId) {
      localStorage.setItem(META_CAMPAIGN_KEY, selectedCampaignId);
      console.log("Selected campaign ID saved:", selectedCampaignId);
    } else {
      localStorage.removeItem(META_CAMPAIGN_KEY);
    }
  }, [selectedCampaignId]);
  
  // Save connection state to localStorage
  useEffect(() => {
    localStorage.setItem(META_CONNECTED_KEY, isConnected ? 'true' : 'false');
  }, [isConnected]);
  
  // Save last token check time
  useEffect(() => {
    if (lastTokenCheck > 0) {
      localStorage.setItem(META_TOKEN_CHECK_KEY, lastTokenCheck.toString());
    }
  }, [lastTokenCheck]);

  const setConnected = (connected: boolean) => {
    setIsConnected(connected);
    
    if (connected) {
      // Reset connection errors on successful connection
      setConnectionErrors(0);
    } else {
      // If disconnecting, clear the token
      setAccessToken(null);
      setSelectedCampaignId(null);
    }
  };

  const incrementConnectionErrors = () => {
    setConnectionErrors(prev => prev + 1);
  };
  
  const resetConnectionErrors = () => {
    setConnectionErrors(0);
  };

  const disconnect = () => {
    console.log("Disconnecting Meta account");
    localStorage.removeItem(META_TOKEN_KEY);
    localStorage.removeItem(META_CAMPAIGN_KEY);
    localStorage.removeItem(META_CONNECTED_KEY);
    localStorage.removeItem(META_TOKEN_CHECK_KEY);
    localStorage.removeItem('meta_auth_state'); // Also clear the state parameter
    setAccessToken(null);
    setSelectedCampaignId(null);
    setIsConnected(false);
    setConnectionErrors(0);
  };

  return (
    <MetaContext.Provider value={{ 
      isConnected, 
      isConnecting,
      accessToken, 
      selectedCampaignId,
      setConnected, 
      setAccessToken,
      setSelectedCampaignId,
      setIsConnecting,
      disconnect,
      lastTokenCheck,
      setLastTokenCheck,
      connectionErrors,
      incrementConnectionErrors,
      resetConnectionErrors
    }}>
      {children}
    </MetaContext.Provider>
  );
};

export const useMeta = () => {
  const context = useContext(MetaContext);
  if (context === undefined) {
    throw new Error('useMeta must be used within a MetaProvider');
  }
  return context;
};
